/* New Login 工具函数：供 new-login 相关组件复用 */

/**
 * 验证邮箱格式（通用国际邮箱基础规则）
 * 规则说明：
 * - 必须包含「@」与「.」
 * - 允许常见的本地命名字符（字母、数字及部分符号）
 * - 不做过度严格限制，以避免误伤合法的海外邮箱
 * @param {string} email - 邮箱地址
 * @returns {boolean} 是否为有效邮箱
 */
export function validateEmail(email) {
  if (!email) return false;
  const value = String(email).trim();
  // 简洁且兼容性较好的邮箱校验
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  return emailRegex.test(value);
}

/**
 * 规范化手机号：去除空格、连字符等非数字字符，仅保留数字
 * @param {string} mobile - 原始手机号
 * @returns {string} 仅包含数字的手机号
 */
export function normalizeMobile(mobile) {
  if (!mobile) return '';
  return String(mobile).replace(/[^\d]/g, '');
}

/**
 * 验证手机号基础格式（国际通用，基础规则）
 * 规则说明：
 * - 去除空格、短横线等后，仅允许数字
 * - 长度在 4–15 位之间（参考 E.164 的常见长度区间）
 *   - 过短一般为误输
 *   - 超过 15 位通常为异常数据
 * @param {string} mobile - 手机号
 * @returns {boolean} 是否为有效手机号
 */
export function validateMobile(mobile) {
  const normalized = normalizeMobile(mobile);
  if (!normalized) return false;
  // 4~15 位纯数字
  const mobileRegex = /^\d{4,15}$/;
  return mobileRegex.test(normalized);
}

/**
 * 根据国家码 + 手机号做基础格式验证
 * 要求：
 * - 国家码存在且非空
 * - 手机号通过 validateMobile 校验
 * @param {string|number} countryCode - 国家码（如 "+86" 或 "86" 或 "44"）
 * @param {string} mobile - 手机号
 * @returns {boolean} 是否为有效手机号
 */
export function validateMobileWithCountry(countryCode, mobile) {
  const hasCountry =
    countryCode !== null &&
    countryCode !== undefined &&
    String(countryCode).trim() !== '';
  if (!hasCountry) return false;
  return validateMobile(mobile);
}

/**
 * 验证必填字段
 * @param {*} value - 字段值
 * @param {string} labelKey - 字段标签（用于错误消息）
 * @returns {Object} { valid: boolean, message: string }
 */
export function validateRequired(value, labelKey = '字段') {
  if (!value || (typeof value === 'string' && value.trim().length === 0)) {
    return {
      valid: false,
      message: `${labelKey}不能为空`
    };
  }
  return { valid: true, message: '' };
}

/**
 * 验证手机号（仅数字）
 * @param {string} mobile - 手机号
 * @param {string} labelKey - 字段标签（用于错误消息）
 * @returns {Object} { valid: boolean, message: string }
 */
export function validateMobileNumber(mobile, labelKey = '手机号') {
  if (!mobile || mobile.trim().length === 0) {
    return {
      valid: false,
      message: `${labelKey}不能为空`
    };
  }
  
  // 只允许数字
  if (!/^\d+$/.test(mobile)) {
    return {
      valid: false,
      message: `${labelKey}只能包含数字`
    };
  }
  
  return { valid: true, message: '' };
}

/**
 * 验证邮箱格式（带错误消息）
 * @param {string} email - 邮箱地址
 * @param {string} labelKey - 字段标签（用于错误消息）
 * @returns {Object} { valid: boolean, message: string }
 */
export function validateEmailWithMessage(email, labelKey = '邮箱') {
  if (!email || email.trim().length === 0) {
    return {
      valid: false,
      message: `${labelKey}不能为空`
    };
  }
  
  if (!validateEmail(email)) {
    return {
      valid: false,
      message: `${labelKey}格式不正确`
    };
  }
  
  return { valid: true, message: '' };
}

/**
 * 检查密码强度
 * @param {string} password - 密码
 * @returns {string} 密码强度：'weak', 'medium', 'strong'
 * 
 * 规则：
 * - 弱 (weak): 只有数字或英文字符，密码长度达6个字元
 * - 中等 (medium): 使用数字、英文字符及符号的混合 - 两种类型
 * - 强 (strong): 使用数字、英文字符及符号的混合 - 三种类型
 */
export function checkPasswordStrength(password) {
  if (!password) return '';
  
  // 检查长度（至少6个字符）
  if (password.length < 6) {
    return 'weak';
  }
  
  // 检查是否包含不同类型的字符
  const hasNumber = /\d/.test(password);
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasSymbol = /[^a-zA-Z\d]/.test(password);
  
  // 计算包含的类型数量
  let typeCount = 0;
  if (hasNumber) typeCount++;
  if (hasLetter) typeCount++;
  if (hasSymbol) typeCount++;
  
  // 判断强度
  if (typeCount === 1) {
    // 只有一种类型（只有数字或只有英文字符），且长度>=6，强度为 weak
    return 'weak';
  } else if (typeCount === 2) {
    // 两种类型（数字+英文 或 英文+符号 或 数字+符号），强度为 medium
    return 'medium';
  } else if (typeCount === 3) {
    // 三种类型都有（数字+英文+符号），强度为 strong
    return 'strong';
  } else {
    // 没有类型（理论上不应该发生），返回 weak
    return 'weak';
  }
}

/**
 * 格式化手机号显示（添加国家码）
 * @param {string} countryCode - 国家码
 * @param {string} mobileNumber - 手机号
 * @returns {string} 格式化后的手机号
 */
export function formatMobileNumber(countryCode, mobileNumber) {
  return `${countryCode} ${mobileNumber}`;
}

/**
 * 创建错误清除方法的工厂函数
 * @param {string} errorPropertyName - 错误属性名称（如 'usernameError'）
 * @returns {Function} 错误清除方法
 */
export function createClearErrorMethod(errorPropertyName) {
  return function() {
    this[errorPropertyName] = false;
  };
}

/**
 * 处理 OTP 输入
 * @param {Array} otpArray - OTP 数组引用
 * @param {number} index - 当前输入框索引
 * @param {Event} event - 输入事件
 * @param {Function} onComplete - 完成回调函数（当所有位都填满时调用）
 * @param {Function} clearError - 清除错误的方法
 * @param {Function} getNextInput - 获取下一个输入框的方法
 */
export function handleOtpInput(otpArray, index, event, onComplete, clearError, getNextInput) {
  const value = event.target.value.replace(/[^0-9]/g, '').slice(0, 1);
  otpArray[index] = value;
  
  // 清除错误状态
  if (clearError) {
    clearError();
  }
  
  // 自动聚焦到下一个输入框（如果有值且不是最后一个）
  if (value && index < 3) {
    // 使用 setTimeout 确保 DOM 更新后再聚焦，使体验更流畅
    setTimeout(() => {
      const nextInput = getNextInput ? getNextInput(index + 1) : null;
      if (nextInput) {
        nextInput.focus();
        // 选中下一个输入框的内容，方便用户直接替换
        nextInput.select();
      }
    }, 0);
  }
  
  // 检查是否所有位都填满了
  const allFilled = otpArray.length === 4 && otpArray.every(d => d && d.length > 0);
  
  if (allFilled && onComplete) {
    // 使用 setTimeout 确保所有输入框的值都已更新，然后触发验证
    setTimeout(() => {
      onComplete();
    }, 100);
  }
}

/**
 * 处理 OTP 键盘事件（支持 Backspace 回退）
 * @param {Array} otpArray - OTP 数组引用
 * @param {number} index - 当前输入框索引
 * @param {Event} event - 键盘事件
 * @param {Function} getPrevInput - 获取上一个输入框的方法
 */
export function handleOtpKeydown(otpArray, index, event, getPrevInput) {
  if (event.key === 'Backspace' && !otpArray[index] && index > 0) {
    const prevInput = getPrevInput ? getPrevInput(index - 1) : null;
    if (prevInput) {
      prevInput.focus();
    }
  }
}

/**
 * 处理 OTP 粘贴事件（支持一次性粘贴4位数字）
 * @param {Array} otpArray - OTP 数组引用
 * @param {Event} event - 粘贴事件
 * @param {Function} onComplete - 完成回调函数（当粘贴4位数字时调用）
 * @param {Function} getNextInput - 获取下一个输入框的方法
 */
export function handleOtpPaste(otpArray, event, onComplete, getNextInput) {
  event.preventDefault();
  const pasteData = event.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 4);
  
  for (let i = 0; i < 4; i++) {
    otpArray[i] = '';
  }
  
  pasteData.split('').forEach((char, i) => {
    if (i < 4) {
      otpArray[i] = char;
    }
  });
  
  for (let i = pasteData.length; i < 4; i++) {
    otpArray[i] = '';
  }
  
  if (pasteData.length === 4 && onComplete) {
    // 先显示验证码，500毫秒后再验证准确性
    setTimeout(() => {
      onComplete();
    }, 500);
  } else if (getNextInput && pasteData.length > 0) {
    const nextIndex = Math.min(pasteData.length, 3);
    setTimeout(() => {
      const nextInput = getNextInput(nextIndex);
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    }, 0);
  }
}

/**
 * 从选项数组中查找匹配的选项并返回其文本
 * @param {Array} options - 选项数组，每个选项包含 value 和 text 属性
 * @param {string|number} value - 要查找的值
 * @returns {string} 匹配选项的文本，如果未找到则返回空字符串
 */
export function findOptionText(options, value) {
  if (!options || !Array.isArray(options) || value === undefined || value === null) {
    return '';
  }
  const selected = options.find(option => option.value === value);
  return selected ? selected.text : '';
}

/**
 * 处理表单提交结果
 * @param {Object} result - 提交结果对象
 * @param {Object} statusCodes - 状态码常量对象（如 SUBMIT_STATUS_CODE）
 * @param {Object} callbacks - 回调函数对象
 * @param {Function} callbacks.onCaptchaFailed - 验证码失败回调
 * @param {Function} callbacks.onValidationError - 验证错误回调
 * @param {Function} callbacks.onSuccess - 成功回调
 * @param {Function} callbacks.onError - 其他错误回调
 * @returns {boolean} 是否处理成功
 */
export function handleFormSubmitResult(result, statusCodes, callbacks = {}) {
  const {
    onCaptchaFailed = () => {},
    onValidationError = () => {},
    onSuccess = () => {},
    onError = () => {}
  } = callbacks;

  if (result.statusCode === statusCodes.CAPTCHA_FAILED) {
    alert('验证码验证失败，请重试');
    onCaptchaFailed();
    return false;
  } else if (result.statusCode === statusCodes.VALIDATION_ERROR) {
    let msg = '';
    if (result.Msg && result.Msg.length > 0) {
      msg = result.Msg.join(',');
    }
    alert('以下数据不能为空: ' + msg);
    onValidationError();
    return false;
  } else if (result.statusCode === statusCodes.SUCCESS) {
    onSuccess();
    return true;
  } else {
    onError();
    return false;
  }
}

